import * as React from 'react';
import { useNotification } from 'src/hooks';
import { uploadCustomerCentreFilePromise } from 'src/utils/api/customer-centre/upload-customer-centre-file';
import { fileToBase64Async } from 'src/utils';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY_MAP } from 'src/constants';
import {
  CustomerCentreActiveUploadItem,
  onCustomerCentreUploadFailedCallback,
  onCustomerCentreUploadSuccessCallback,
} from './customer-centre-file-upload-store';

export interface FloatingCustomerCentreFileUploadsItemProps {
  activeUpload: CustomerCentreActiveUploadItem;
}

export const FloatingCustomerCentreFileUploadsItem: React.FunctionComponent<
  FloatingCustomerCentreFileUploadsItemProps
> = ({ activeUpload }) => {
  const dataRef = React.useRef<CustomerCentreActiveUploadItem>(activeUpload);

  const { enqueueSuccessNotification, enqueueErrorNotification } = useNotification();

  const onceRef = React.useRef(false);

  const queryClient = useQueryClient();

  if (!dataRef.current) return <div>&nbsp;</div>;

  const handleCustomerCentreFileUpload = async () => {
    if (!dataRef.current || !dataRef.current.file) {
      enqueueErrorNotification('Something went wrong, found missing upload entry.');

      console.error(activeUpload);
      onCustomerCentreUploadFailedCallback(dataRef.current.id);
      return;
    }

    try {
      // convert file to base64
      const base64 = await fileToBase64Async(dataRef.current.file);

      if (!base64) {
        enqueueErrorNotification('Failed to convert file to base64', { key: `customer-centre-file-upload-error` });
        onCustomerCentreUploadFailedCallback(dataRef.current.id);
        return;
      }

      // upload base64 file
      const isUploadSuccessful = await uploadCustomerCentreFilePromise({
        base64,
        fileName: dataRef.current.file.name,
        type: dataRef.current.file.type,
        org_id: dataRef.current.org_id,
      });

      if (!isUploadSuccessful) {
        enqueueErrorNotification('Failed to upload file', { key: `customer-centre-file-upload-error` });
        onCustomerCentreUploadFailedCallback(dataRef.current.id);
        return;
      }

      // refetch folder files list
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_MAP.useListCustomerCentreFolderFiles, dataRef.current.org_id],
      });

      // refetch folders list -- void operation
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_MAP.useListCustomerCentreFolders],
      });

      enqueueSuccessNotification(`${dataRef.current.file.name} uploaded successfully`, {
        key: `customer-centre-file-upload-success`, // prevent duplicate notifications
      });

      onCustomerCentreUploadSuccessCallback(dataRef.current.id);
    } catch (e) {
      console.error('🚀 ~ FloatingCustomerCentreFileUploadsItem ~ handleCustomerCentreFileUpload e:', e);

      onCustomerCentreUploadFailedCallback(activeUpload.id);
    }
  };

  if (!onceRef.current) {
    onceRef.current = true;
    void handleCustomerCentreFileUpload();
  }

  return <div>&nbsp;</div>;
};
